import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, Avatar, Searchbar, useTheme, ActivityIndicator, Chip, Divider, Button, Snackbar } from 'react-native-paper';
import { getAllChildren } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useFocusEffect } from '@react-navigation/native';

const ChildrenListScreen = ({ navigation }) => {
  const theme = useTheme();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchChildren = async () => {
    try {
      setLoading(true);
      const response = await getAllChildren();
      if (response.success) {
        const processedChildren = (response.data || []).map(child => ({
          ...child,
          name: `${child.first_name} ${child.last_name}`,
          birth_date: child.birthdate
        }));
        setChildren(processedChildren);
        setFilteredChildren(processedChildren);
      } else {
        setSnackbarMessage('Çocuk listesi alınırken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error fetching children:', error);
      setSnackbarMessage('Çocuk listesi alınırken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchChildren();
  }, []);
  
  // Ekran her odaklandığında çalışacak, özellikle çocuk silindikten sonra otomatik yenileme yapacak
  useFocusEffect(
    React.useCallback(() => {
      //console.log('ChildrenListScreen focused, refreshing list');
      fetchChildren();
      // Cleanup function
      return () => {};
    }, [])
  );

  useEffect(() => {
    if (searchQuery) {
      const filtered = children.filter(child => 
        child.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        child.parent?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        child.parent?.username?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChildren(filtered);
    } else {
      setFilteredChildren(children);
    }
  }, [searchQuery, children]);

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    fetchChildren();
  }, []);

  const navigateToChildDetail = (child) => {
    navigation.navigate('ChildDetail', { childId: child.id, childName: child.name });
  };

  const formatBirthDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    } catch (error) {
      return dateString;
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    try {
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return `${age} yaş`;
    } catch (error) {
      return '-';
    }
  };

  const renderChildItem = ({ item: child }) => (
    <Card style={styles.card} onPress={() => navigateToChildDetail(child)}>
      <Card.Content>
        <View style={styles.childHeader}>
          <Avatar.Icon 
            size={50} 
            icon={child.gender === 'male' ? "human-male-boy" : "human-female-girl"} 
            color="white"
            style={{ backgroundColor: 'rgb(125, 76, 236)', elevation: 3 }}
          />
          
          <View style={styles.childInfo}>
            <Text style={styles.childName}>{child.name}</Text>
            <Text style={styles.childAge}>{calculateAge(child.birth_date)}</Text>
            
            <Chip 
              icon={child.active_visit ? "timer-sand" : "timer-off"}
              style={[styles.statusChip, {backgroundColor: child.active_visit ? 'rgba(125, 76, 236, 0.2)' : 'rgba(125, 76, 236, 0.1)'}]}
            >
              <Text style={{color: 'rgb(125, 76, 236)'}}>{child.active_visit ? 'Şu anda parkta' : 'Parkta değil'}</Text>
            </Chip>
          </View>
        </View>
        
        <Divider style={styles.divider} />
        
        <View style={[styles.childDetails, {backgroundColor: 'rgba(255, 255, 255, 0.85)', padding: 8, borderRadius: 8}]}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Ebeveyn:</Text>
            <Text style={styles.detailValue}>{child.parent?.parent_name
             || child.parent_name || '-'}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Doğum Tarihi:</Text>
            <Text style={styles.detailValue}>{formatBirthDate(child.birth_date)}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Cinsiyet:</Text>
            <Text style={styles.detailValue}>{child.gender === 'male' ? 'Erkek' : 'Kız'}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Toplam Ziyaret:</Text>
            <Text style={styles.detailValue}>{child.visit_count || 0}</Text>
          </View>
        </View>
        
        <View style={[styles.actionButtons, {backgroundColor: 'rgba(255, 255, 255, 0.85)', padding: 8, borderRadius: 8, marginTop: 8}]}>
          <Button 
            mode="contained" 
            icon="account-details" 
            onPress={() => navigateToChildDetail(child)}
            style={{backgroundColor: 'rgb(125, 76, 236)'}}
            buttonColor="rgb(125, 76, 236)"
            textColor="white"
            labelStyle={{fontWeight: 'bold'}}
          >
            Detaylar
          </Button>
          
        
            {/*
              {child.active_visit ? (
              <Button 
              mode="outlined" 
              icon="exit-run" 
              color="#FF9800"
              style={styles.exitButton}
            >
              Çıkış Yap
            </Button>
          ) : (
            <Button 
              mode="outlined" 
              icon="login" 
              color="#4CAF50"
              style={styles.enterButton}
            >
              Giriş Yap
            </Button>
           )} */}
          
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Çocuk veya Ebeveyn Ara..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
        iconColor="rgb(125, 76, 236)"
        inputStyle={{color: 'rgb(90, 50, 180)'}}
        theme={{ colors: { primary: 'rgb(125, 76, 236)' }}}
      />

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
          <Text style={[styles.loadingText, {color: 'rgb(125, 76, 236)'}]}>Çocuklar yükleniyor...</Text>
        </View>
      ) : filteredChildren.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="account-child-outline" size={80} color="rgb(125, 76, 236)" />
          <Text style={[styles.emptyText, {color: 'rgb(125, 76, 236)'}]}>Çocuk bulunamadı</Text>
        </View>
      ) : (
        <FlatList
          data={filteredChildren}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderChildItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl 
              refreshing={refreshing} 
              onRefresh={onRefresh} 
              colors={['rgb(125, 76, 236)']} 
              tintColor="rgb(125, 76, 236)"
            />
          }
        />
      )}

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: 'rgba(255, 255, 255, 0.95)'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: 'white'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  searchbar: {
    marginHorizontal: 16,
    marginVertical: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
  },
  listContent: {
    padding: 8,
  },
  card: {
    marginVertical: 6,
    marginHorizontal: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  childHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  childInfo: {
    marginLeft: 16,
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: 8,
    borderRadius: 8,
  },
  childName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  childAge: {
    marginTop: 2,
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
  },
  statusChip: {
    marginTop: 8,
    height: 26,
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(125, 76, 236, 0.2)',
  },
  divider: {
    marginVertical: 12,
    backgroundColor: 'rgba(125, 76, 236, 0.2)',
  },
  childDetails: {
    marginBottom: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 8,
  },
  detailRow: {
    flexDirection: 'row',
    marginVertical: 2,
  },
  detailLabel: {
    width: 100,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  detailValue: {
    flex: 1,
    color: 'rgb(90, 50, 180)',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 8,
  },
  exitButton: {
    borderColor: '#FF9800',
  },
  enterButton: {
    borderColor: '#4CAF50',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  emptyText: {
    fontSize: 18,
    marginTop: 16,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
});

export default ChildrenListScreen;
